import { cn } from '#/shared/lib/utils'

import type { Tone } from '../config/channel-config'

type IconComponent = React.ComponentType<{ className?: string }>

const RING_SIZE = 32
const RING_STROKE = 2.88
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

interface ActivityIndicatorProps {
  Icon: IconComponent
  tone: Tone
  progress: number
}

export function ActivityIndicator({
  Icon,
  tone,
  progress,
}: ActivityIndicatorProps) {
  const clamped = Math.min(1, Math.max(0, progress))
  const offset = RING_CIRCUMFERENCE * (1 - clamped)

  return (
    <span
      className={cn(
        'relative flex size-8 shrink-0 items-center justify-center rounded-full',
        tone.bg,
      )}
    >
      <svg
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        fill="none"
        className="absolute inset-0 size-full -rotate-90"
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          className="stroke-warm-100"
        />
        {clamped > 0 && (
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={offset}
            className={cn(
              'transition-[stroke-dashoffset] duration-500',
              tone.stroke,
            )}
          />
        )}
      </svg>
      <Icon className={cn('relative size-4', tone.icon)} />
    </span>
  )
}
